import React from 'react';
import {
  Box,
  Container,
  Text,
  Button,
  VStack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  useColorModeValue
} from '@chakra-ui/react';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('Error caught by boundary:', error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorFallback 
          error={this.state.error}
          resetError={() => this.setState({ hasError: false, error: null, errorInfo: null })}
        />
      );
    }

    return this.props.children;
  }
}

const ErrorFallback = ({ error, resetError }) => {
  const bgColor = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');

  return (
    <Box bg={bgColor} minH="100vh" py={16}>
      <Container maxW="600px">
        <VStack spacing={8} textAlign="center">
          <Alert
            status="error"
            variant="subtle"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            height="200px"
            bg={cardBg}
            borderRadius="lg"
            boxShadow="sm"
          >
            <AlertIcon boxSize="40px" mr={0} />
            <AlertTitle mt={4} mb={1} fontSize="lg">
              Something went wrong!
            </AlertTitle>
            <AlertDescription maxWidth="sm">
              We're sorry, but something unexpected happened. Please try refreshing the page or contact support if the problem persists.
            </AlertDescription>
          </Alert>

          <VStack spacing={4}>
            <Button
              colorScheme="blue"
              onClick={() => window.location.reload()}
              size="lg"
            >
              Refresh Page
            </Button>
            
            <Button
              variant="outline"
              onClick={resetError}
              size="md"
            >
              Try Again
            </Button>
          </VStack>

          {process.env.NODE_ENV === 'development' && error && (
            <Box
              bg="red.50"
              border="1px solid"
              borderColor="red.200"
              borderRadius="md"
              p={4}
              textAlign="left"
              fontSize="sm"
              fontFamily="mono"
              maxW="100%"
              overflow="auto"
            >
              <Text fontWeight="bold" color="red.600" mb={2}>
                Error Details (Development Only):
              </Text>
              <Text color="red.700" whiteSpace="pre-wrap">
                {error.toString()}
              </Text>
            </Box>
          )}
        </VStack>
      </Container>
    </Box>
  );
};

export default ErrorBoundary;
